import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const fiscalStates = {
  preparada: "Preparada · ainda não enviada", enviando: "Enviando", processando: "Em processamento",
  desconhecida: "Resultado incerto · consulte", rejeitada: "Rejeitada", autorizada: "Autorizada",
  contingencia: "Em contingência · consulte", cancelamento_pendente: "Cancelamento em processamento",
  cancelamento_incerto: "Cancelamento incerto · consulte", cancelada: "Cancelada",
};
export function isFiscalAdmin() {
  try { return JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}").role === "admin"; }
  catch { return false; }
}
export function fiscalError(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.codigo === "arquivo_pendente") return "Arquivo pendente. Consulte a nota para recuperar os documentos.";
  return "Não foi possível confirmar a operação. Atualize a conferência ou consulte a nota antes de tentar novamente.";
}
export function FiscalEnvironment({ config }) {
  return <div className="space-y-1 text-sm">
    <p className="font-medium">{config.ambiente === "homologacao" ? "Homologação · sem valor fiscal" : config.ambiente} · {config.transporte === "simulado" ? "Focus simulada" : config.transporte}</p>
    <p>CNPJ do emitente: {config.cnpj || "Não configurado"}</p>
    <p>Credencial: {config.credencial_configurada ? "Configurada" : "Não configurada"}. Envio: {config.emissao_disponivel ? "Habilitado" : "Desligado"}.</p>
  </div>;
}
export function FiscalSettings() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const pending = useRef(false);
  useEffect(() => {
    let active = true;
    if (isFiscalAdmin()) api.get("/fiscal/config").then(r => { if (active) setConfig(r.data); })
      .catch(e => { if (active) setError(fiscalError(e)); });
    return () => { active = false; };
  }, []);
  async function changeEmission() {
    if (pending.current || !config || (!config.emissao_disponivel && (!confirmed || !config.configuracao_pronta))) return;
    pending.current = true; setSaving(true); setError(""); setMessage("");
    try {
      const { ambiente, cnpj, transporte, versao } = config;
      const response = await api.post("/fiscal/config/emissao", {
        ambiente, cnpj, transporte, versao, habilitado: !config.emissao_disponivel,
      });
      setConfig(response.data);
      setMessage(response.data.emissao_disponivel ? "Emissão manual habilitada. Emita cada nota na compra correspondente." : "Emissão desabilitada. Novos envios estão bloqueados.");
    } catch (e) {
      setError(fiscalError(e));
      try { setConfig((await api.get("/fiscal/config")).data); }
      catch { setConfig(null); }
    } finally {
      setConfirmed(false); setSaving(false); pending.current = false;
    }
  }
  return <section className="space-y-4 p-5 bg-card rounded-xl border border-border">
    <h2 className="text-lg font-semibold">NFC-e</h2>
    {!isFiscalAdmin() ? <p>Acesso fiscal restrito a administradores.</p> : <>
      {error && <p role="alert">{error}</p>}
      {config ? <>
        <FiscalEnvironment config={config} />
        <div className="space-y-3 border-t border-border pt-4">
          <p className="text-sm">A emissão é manual, por compra. Habilitar este controle não emite notas automaticamente.</p>
          {!config.emissao_disponivel && <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={confirmed} onCheckedChange={value => setConfirmed(value === true)} disabled={saving || !config.configuracao_pronta} aria-label="Conferi os requisitos para habilitar a emissão" />
            <span>Conferi o certificado, o credenciamento e os dados fiscais dos produtos. {config.ambiente === "producao" ? "Entendo que as próximas emissões terão valor fiscal." : "As emissões serão de homologação, sem valor fiscal."}</span>
          </label>}
          {!config.configuracao_pronta && <p className="text-sm text-muted-foreground">Complete a configuração do emitente e da credencial antes de habilitar.</p>}
          <Button type="button" variant={config.emissao_disponivel ? "outline" : "default"} onClick={changeEmission}
            disabled={saving || (!config.emissao_disponivel && (!confirmed || !config.configuracao_pronta))}>
            {saving ? "Salvando…" : config.emissao_disponivel ? "Desabilitar emissão" : "Habilitar emissão manual"}
          </Button>
          <p className="text-sm text-muted-foreground">Desabilitar bloqueia novos envios. Notas já em processamento continuam consultáveis.</p>
        </div>
      </> : !error && <p role="status">Carregando configuração fiscal…</p>}
      {message && <p role="status">{message}</p>}
    </>}
    <p className="text-sm text-muted-foreground">Confira os códigos em Cardápio → Cadastro fiscal com a contabilidade. A configuração do emitente e as credenciais são feitas no servidor.</p>
  </section>;
}

const brl = value => Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
export default function FiscalOrderDialog({ order, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [cancel, setCancel] = useState(false);
  const [reason, setReason] = useState("");
  const guard = useRef(false);
  const mounted = useRef(true);
  const base = `/orders/${order.id}/nfce`;
  const admin = isFiscalAdmin();

  async function load() {
    setData(null); setChecked(false); setCancel(false);
    const [purchase, history, config] = await Promise.all([
      api.get(`/orders/${order.id}/data`), api.get(base), api.get("/fiscal/config"),
    ]);
    if (mounted.current) setData({ purchase: purchase.data, ...history.data, config: config.data });
  }
  useEffect(() => {
    mounted.current = true;
    if (admin) load().catch(e => { if (mounted.current) setError(fiscalError(e)); });
    return () => { mounted.current = false; };
    // A compra é fixada pelo key do componente nos dois pontos de entrada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(action) {
    if (guard.current) return;
    guard.current = true; setBusy(true); setError(""); setNotice("");
    try { await action(); }
    catch (e) { if (mounted.current) setError(fiscalError(e)); }
    finally { guard.current = false; if (mounted.current) setBusy(false); }
  }
  async function operate(path, payload) {
    // A resposta pode se perder depois do commit; releitura nunca retransmite.
    try {
      const { data: result } = await api.post(path, payload);
      setNotice(result.resultado?.mensagem_sefaz || "Operação registrada. Confira a situação fiscal abaixo.");
    } finally { await load(); }
  }
  async function download(doc, kind) {
    try {
      const response = await api.get(`${base}/documentos/${doc.id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `nfce-${doc.referencia}-${kind.replaceAll("/", "-")}.${kind === "danfe" ? (response.data.type.includes("pdf") ? "pdf" : "html") : "xml"}`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try { e.response.data = JSON.parse(await e.response.data.text()); } catch { /* Usa erro genérico. */ }
      }
      throw e;
    }
  }
  const history = data?.historico || [];
  const doc = history.find(d => d.id === selectedId) || data?.documento;
  const current = doc?.id === data?.documento?.id;
  const canSend = current && doc?.transmissao_disponivel && data?.config.emissao_disponivel;
  return <Dialog open onOpenChange={open => { if (!open && !guard.current) onClose(); }}>
    <DialogContent className="bg-card border-border w-[calc(100%-2rem)] max-w-2xl max-h-[90dvh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>NFC-e · compra {order.id}</DialogTitle>
        <DialogDescription>Conferência e emissão manual. A comanda de preparo é impressa separadamente e não é um documento fiscal.</DialogDescription>
      </DialogHeader>
      {!admin ? <p>Acesso fiscal restrito a administradores.</p> : <>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {notice && <p role="status" className="text-sm">{notice}</p>}
        <Button variant="outline" disabled={busy} onClick={() => run(load)}>Atualizar conferência</Button>
        {!data ? <p role="status">{error ? "Dados indisponíveis. Atualize para continuar." : "Carregando conferência…"}</p> : <>
          <FiscalEnvironment config={data.config} />
          <section className="space-y-2 border-t border-border pt-4">
            <h3 className="font-semibold">Compra atual · versão {data.purchase.versao}</h3>
            <p>{data.purchase.nomeCliente}</p>
            <p className="text-sm">{data.purchase.enderecoCliente || "Endereço não informado"} · CPF na nota: {data.purchase.cpf_nota || "Não informado"}</p>
            <ul className="text-sm space-y-1">{(data.purchase.itens || []).map((item, i) => <li key={i}>{item.quantidade} × {item.nome} · {brl(item.valor_unitario)} cada</li>)}</ul>
            {!data.purchase.itens?.length && <p className="text-sm">Sem itens estruturados. Corrija a compra antes de preparar a NFC-e.</p>}
            <p className="text-sm">Entrega: {brl(data.purchase.taxa_entrega)} · Desconto: {brl(data.purchase.desconto)} · Total: <strong>{brl(data.purchase.Valor_Total)}</strong></p>
            <p className="text-sm">Pagamentos: {(data.purchase.pagamentos || []).map(p => `${p.metodo}: ${brl(p.valor)}${p.confirmado ? " (confirmado)" : " (não confirmado)"}${["debito", "credito"].includes(p.metodo) ? p.tipo_integracao === "2" ? " · maquininha independente (POS)" : " · confirmação de POS pendente" : ""}`).join("; ") || "Não estruturados"}. Troco: {brl(data.purchase.troco)}.</p>
          </section>
          {history.length > 0 && <section className="space-y-3 border-t border-border pt-4">
            <Label htmlFor="fiscal-history">Histórico fiscal desta compra</Label>
            <select id="fiscal-history" className="w-full bg-input rounded-md border border-border p-2 text-sm" value={doc?.id || ""} disabled={busy}
              onChange={e => { setSelectedId(e.target.value); setChecked(false); setCancel(false); setReason(""); }}>
              {history.map(d => <option key={d.id} value={d.id}>{fiscalStates[d.situacao] || d.situacao} · {d.referencia}</option>)}
            </select>
            <p className="font-semibold" role="status">{fiscalStates[doc.situacao] || doc.situacao}</p>
            <p className="text-xs text-muted-foreground break-all">Referência: {doc.referencia}. Versão fiscal {doc.versao_fiscal}. Compra reservada na versão {doc.versao_pedido}; envio na versão {doc.versao_pedido_envio || "—"}. Os itens acima são da compra atual, não do snapshot fiscal arquivado.</p>
            <Button variant="outline" disabled={busy} onClick={() => run(() => operate(`${base}/consultar`, { documento_id: doc.id }))}>Consultar situação e arquivos</Button>
            <div className="flex flex-wrap gap-2">{(doc.arquivos || []).map(file => <Button key={file.tipo} variant="outline" disabled={busy || file.estado !== "pronto"}
              onClick={() => run(() => download(doc, file.tipo === "cancelamento_xml" ? "cancelamento/xml" : file.tipo))}>
              {file.tipo === "danfe" ? "Baixar DANFE" : file.tipo === "xml" ? "Baixar XML" : "Baixar XML do cancelamento"}{file.estado !== "pronto" ? " · pendente" : ""}
            </Button>)}</div>
            <p className="text-xs text-muted-foreground">Se um arquivo estiver ausente ou pendente, consulte para recuperá-lo. Uma falha no arquivo não exige emitir outra nota.</p>
          </section>}
          {(!data.documento || data.documento.situacao === "cancelada" || canSend) && <section className="space-y-3 border-t border-border pt-4">
            <div className="flex items-start gap-2"><Checkbox id="fiscal-reviewed" checked={checked} disabled={busy}
              onCheckedChange={value => setChecked(value === true)} /><Label htmlFor="fiscal-reviewed" className="text-sm leading-relaxed">Conferi itens, entrega, pagamentos e cadastro fiscal desta versão da compra.</Label></div>
            {!data.documento || data.documento.situacao === "cancelada" ? <Button disabled={busy || !checked || !data.purchase.itens?.length}
              onClick={() => run(async () => { setSelectedId(""); await operate(base, { versao: data.purchase.versao }); })}>Preparar NFC-e sem transmitir</Button> :
              <Button disabled={busy || !checked} onClick={() => run(() => operate(`${base}/emitir`, { documento_id: doc.id, versao: data.purchase.versao, versao_fiscal: doc.versao_fiscal }))}>Confirmar transmissão · {data.config.transporte}</Button>}
          </section>}
          {current && doc?.situacao === "autorizada" && <section className="space-y-3 border-t border-border pt-4">
            {!cancel ? <Button variant="outline" disabled={busy} onClick={() => setCancel(true)}>Solicitar cancelamento</Button> : <>
              <Label htmlFor="fiscal-reason">Justificativa do cancelamento (15 a 255 caracteres)</Label>
              <Input id="fiscal-reason" value={reason} maxLength={255} disabled={busy} onChange={e => setReason(e.target.value)} />
              <p className="text-sm">Confirme o cancelamento desta nota. Esta ação não cancela a compra nem devolve pagamentos.</p>
              <Button variant="destructive" disabled={busy || reason.trim().length < 15} onClick={() => run(() => operate(`${base}/cancel`, { documento_id: doc.id, versao_fiscal: doc.versao_fiscal, justificativa: reason.trim() }))}>Confirmar cancelamento da NFC-e</Button>
            </>}
          </section>}
          {data.documento && !["rejeitada", "cancelada"].includes(data.documento.situacao) && <p className="text-sm text-muted-foreground">Alterações comerciais estão bloqueadas. A compra pode continuar no preparo e na entrega. Compras com histórico fiscal não podem ser excluídas.</p>}
        </>}
      </>}
    </DialogContent>
  </Dialog>;
}
